import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import { MaterialIcon } from '@/components/common/MaterialIcon';
import { MashrabiyaPattern } from '@/components/common/MashrabiyaPattern';
import { Container } from '@/components/common/Container';
import { Logo } from '@/components/common/Logo';
import { useBrandInfo } from '@/hooks/useBrandInfo';

interface FooterLink {
  label: string;
  to: string;
}

interface ContactItem {
  icon: string;
  label: string;
  href: string;
  /** When true, opens the destination in a new tab (external maps URL). */
  external?: boolean;
}

const QUICK_LINKS: FooterLink[] = [
  { label: 'About Us', to: '/about' },
  { label: 'Zakat Portal', to: '/#calculate-zakat' },
  { label: 'Prayer Schedule', to: '/prayer-times' },
];

const COMMUNITY: FooterLink[] = [
  { label: 'Volunteer', to: '/community' },
  { label: 'Events Calendar', to: '/events' },
  { label: 'Membership', to: '/auth/register' },
];

function buildContactItems(email: string): ContactItem[] {
  return [
    {
      icon: 'location_on',
      label: '420 Stephenson Rd, Stephenson, VA 22656',
      href: 'https://maps.google.com/?q=420+Stephenson+Rd,+Stephenson,+VA+22656',
      external: true,
    },
    {
      icon: 'call',
      label: '540-313-6087',
      href: 'tel:5403136087',
    },
    {
      icon: 'mail',
      label: email,
      href: `mailto:${email}`,
    },
  ];
}

/**
 * Site footer composed of brand block, quick links + community link lists,
 * and a contact column. Mirrors the Stitch desktop footer styling.
 */
export function Footer() {
  return (
    <footer className="relative w-full border-t border-outline-variant/30 bg-[#fff9eb] pb-24 md:pb-0">
      <MashrabiyaPattern className="opacity-5" />

      <Container className="relative z-10 grid grid-cols-1 gap-gutter py-12 md:grid-cols-4">
        <div>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Scroll to top of page"
            className="mb-6 inline-flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed-dim focus-visible:ring-offset-2 focus-visible:ring-offset-[#fff9eb]"
          >
            <Logo size={56} asLink={false} />
          </button>
          <p className="font-body-md text-body-md text-on-surface-variant">
            MCCWA is a U.S. nonprofit organization with 501(c)(3) tax-exempt status.
          </p>
        </div>

        <FooterColumn title="Quick Links" links={QUICK_LINKS} />
        <FooterColumn title="Community" links={COMMUNITY} />
        <ContactColumn />
      </Container>

      <div className="border-t border-outline-variant/20 py-8">
        <Container className="relative flex flex-col items-center gap-4 md:grid md:grid-cols-3 md:gap-0">
          <div className="flex items-center gap-3 md:col-start-1 md:justify-self-start">
            <span className="font-label-caps text-label-caps text-on-surface">Follow us!</span>
            <div className="flex items-center gap-2">
              <SocialIcon
                href="https://www.facebook.com/profile.php?id=61557094544027"
                label="MCCWA on Facebook"
              >
                <Facebook className="h-4 w-4" fill="currentColor" />
              </SocialIcon>
              <SocialIcon href="https://instagram.com" label="MCCWA on Instagram">
                <Instagram className="h-4 w-4" />
              </SocialIcon>
              <SocialIcon
                href="https://api.whatsapp.com/send?phone=%2B15403136087"
                label="MCCWA on WhatsApp"
              >
                <WhatsAppIcon className="h-4 w-4" />
              </SocialIcon>
              <SocialIcon href="https://youtube.com" label="MCCWA on YouTube">
                <Youtube className="h-4 w-4" fill="currentColor" />
              </SocialIcon>
            </div>
          </div>

          <p className="font-label-caps text-label-caps text-outline md:col-start-2 md:justify-self-center">
            MCCWA © Copyright - {new Date().getFullYear()}
          </p>

          <AlgotechCredit />
        </Container>
      </div>
    </footer>
  );
}

/**
 * "Designed and developed by [Algotech]" credit link. Renders the logo
 * image when present, falls back to clean styled text if the asset is
 * missing (so the footer never shows a broken-image icon).
 */
function AlgotechCredit() {
  return (
    <div className="inline-flex items-center gap-3 md:col-start-3 md:justify-self-end">
      <span className="font-label-caps text-label-caps text-outline">
        Designed and developed by
      </span>
      <a
        href="https://www.algotechfed.com/"
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Algotech — visit algotechfed.com"
        className="group inline-flex items-center gap-2"
      >
        <img src="/algotech-logo.png" alt="AlgoTech" className="h-6 w-auto" />
        <span className="font-headline-sm text-sm font-bold tracking-tight text-on-surface transition-colors group-hover:text-brand-teal">
          AlgoTech
        </span>
      </a>
    </div>
  );
}

function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <h4 className="mb-6 font-label-caps text-label-caps text-primary">{title.toUpperCase()}</h4>
      <ul className="space-y-4">
        {links.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="inline-block font-body-md text-body-md text-on-surface-variant transition-all hover:translate-x-1 hover:text-primary"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Inline WhatsApp logo — lucide-react doesn't ship a brand glyph. */
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function SocialIcon({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-fixed-dim text-on-secondary-container transition-transform hover:scale-110"
    >
      {children}
    </a>
  );
}

function ContactColumn() {
  const brand = useBrandInfo();
  const items = buildContactItems(brand.contactEmail);
  return (
    <div>
      <h4 className="mb-6 font-label-caps text-label-caps text-primary">CONTACT US</h4>
      <ul className="space-y-4">
        {items.map((item) => (
          <li key={item.href}>
            <a
              href={item.href}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
              className="group flex items-start gap-3 font-body-md text-body-md text-on-surface-variant transition-colors hover:text-primary"
            >
              <MaterialIcon
                name={item.icon}
                className="mt-0.5 shrink-0 text-primary transition-transform group-hover:scale-110"
              />
              <span>{item.label}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
